import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ObjectId } from 'bson';
import moment from 'moment';
import Card from 'components/inputs/Card';
import { GET_DASHBOARD_V2_PERFORMANCE } from 'actions/dashboardV2';
import ChartEmbed from './ChartEmbed';
import PerformanceMetrics from './PerformanceMetrics';
import StationTables from './StationTables';
import { CHART_IDS } from '../chartConstants';

const V2_CHART_IDS = {
  sessions: CHART_IDS.V2_SESSIONS,
  units: CHART_IDS.V2_UNITS,
  energyDaily: CHART_IDS.V2_ENERGY_DAILY,
  energyMonthly: CHART_IDS.V2_ENERGY_MONTHLY,
  activeUsers: CHART_IDS.V2_ACTIVE_USERS,
  newUsers: CHART_IDS.V2_NEW_USERS,
};

const PERIOD_TO_REVENUE_KEY = {
  today: 'today',
  yesterday: 'yesterday',
  '7d': 'lastWeek',
  '30d': 'last30d',
};

const formatRevenue = (value) => {
  if (value == null) return '—';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const PERIODS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: '7d' },
  { label: 'Last 30 Days', value: '30d' },
];

const ACTIVE_USER_PERIODS = [
  { label: 'Today', value: 'today', group: 'Daily' },
  { label: 'Yesterday', value: 'yesterday', group: 'Daily' },
  { label: 'This Week', value: 'this_week', group: 'Weekly' },
  { label: 'Last Week', value: 'last_week', group: 'Weekly' },
  { label: 'This Month', value: 'this_month', group: 'Monthly' },
  { label: 'Last Month', value: 'last_month', group: 'Monthly' },
  { label: 'Last 30 Days', value: '30d', group: 'Total' },
];

const getDateRange = (period) => {
  switch (period) {
    case 'today':
      return { from: moment().startOf('day').toDate(), to: moment().endOf('day').toDate() };
    case 'yesterday':
      return {
        from: moment().subtract(1, 'day').startOf('day').toDate(),
        to: moment().subtract(1, 'day').endOf('day').toDate(),
      };
    case 'this_week':
      return { from: moment().startOf('isoWeek').toDate(), to: moment().endOf('day').toDate() };
    case 'last_week':
      return {
        from: moment().subtract(1, 'week').startOf('isoWeek').toDate(),
        to: moment().subtract(1, 'week').endOf('isoWeek').toDate(),
      };
    case 'this_month':
      return { from: moment().startOf('month').toDate(), to: moment().endOf('day').toDate() };
    case 'last_month':
      return {
        from: moment().subtract(1, 'month').startOf('month').toDate(),
        to: moment().subtract(1, 'month').endOf('month').toDate(),
      };
    case '7d':
      return { from: moment().subtract(6, 'days').startOf('day').toDate(), to: moment().endOf('day').toDate() };
    case '30d':
      return { from: moment().subtract(29, 'days').startOf('day').toDate(), to: moment().endOf('day').toDate() };
    default:
      return { from: moment().startOf('day').toDate(), to: moment().endOf('day').toDate() };
  }
};

const getRangeLabel = (period) => {
  const { from, to } = getDateRange(period);
  return `${moment(from).format('MMM DD')} – ${moment(to).format('MMM DD, YYYY')}`;
};

const last12Months = () => ({
  from: moment().subtract(11, 'months').startOf('month').toDate(),
  to: moment().endOf('month').toDate(),
});

const DashboardV2Section = () => {
  const dispatch = useDispatch();
  const profileData = useSelector((state) => state.profile.userProfile);
  const performance = useSelector((state) => state.dashboardV2?.performance);
  const tenantId = profileData?.tenant?.id;

  const [period, setPeriod] = useState('today');
  const [activeUserPeriod, setActiveUserPeriod] = useState('today');
  const [energyMonth, setEnergyMonth] = useState(moment().format('YYYY-MM'));

  useEffect(() => {
    dispatch({ type: GET_DASHBOARD_V2_PERFORMANCE.REQUEST });
  }, [dispatch]);

  const revenueValue = performance?.[PERIOD_TO_REVENUE_KEY[period]]?.totalRevenue;

  const { from, to } = getDateRange(period);
  const activeUserRange = getDateRange(activeUserPeriod);
  const yearRange = last12Months();

  const tenantFilter = tenantId ? { tenant: { $eq: new ObjectId(tenantId) } } : {};

  const buildFilter = (dateFrom, dateTo) => ({
    ...tenantFilter,
    booking_stop: { $gte: dateFrom, $lte: dateTo },
  });

  const userFilter = (dateFrom, dateTo) => ({
    ...tenantFilter,
    createdAt: { $gte: dateFrom, $lte: dateTo },
  });

  const selectedMonthRange = {
    from: moment(`${energyMonth}-01`).startOf('month').toDate(),
    to: moment(`${energyMonth}-01`).endOf('month').toDate(),
  };

  return (
    <div>
      {/* Period Filter — controls Revenue, Sessions, Units */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {PERIODS.map(({ label, value }) => (
          <button
            key={value}
            onClick={() => setPeriod(value)}
            style={{
              padding: '5px 14px',
              borderRadius: 4,
              border: `1px solid ${period === value ? '#be210b' : '#3b4a56'}`,
              background: period === value ? '#be210b' : 'transparent',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: period === value ? 600 : 400,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Row 1 — Revenue / Sessions / Units stat cards */}
      <Row style={{ marginBottom: 12 }}>
        <Col md={4}>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Revenue</div>
            <div
              style={{
                height: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              {formatRevenue(revenueValue)}
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Sessions</div>
            <ChartEmbed chartId={V2_CHART_IDS.sessions} height="120px" filter={buildFilter(from, to)} />
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Units Consumed (kWh)</div>
            <ChartEmbed chartId={V2_CHART_IDS.units} height="120px" filter={buildFilter(from, to)} />
          </Card>
        </Col>
      </Row>

      {/* Row 2 — Energy Sold Daily (month picker) + Monthly (fixed 12m) */}
      <Row style={{ marginBottom: 12 }}>
        <Col md={6}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 12 }}>
              <span style={{ fontWeight: 600 }}>Energy Sold — Daily</span>
              <input
                type="month"
                value={energyMonth}
                onChange={(e) => setEnergyMonth(e.target.value)}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: '1px solid #3b4a56',
                  background: 'transparent',
                  color: '#fff',
                  fontSize: 13,
                  colorScheme: 'dark',
                }}
              />
            </div>
            <ChartEmbed
              chartId={V2_CHART_IDS.energyDaily}
              height="260px"
              filter={buildFilter(selectedMonthRange.from, selectedMonthRange.to)}
            />
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Energy Sold — Monthly (Last 12 Months)</div>
            <ChartEmbed
              chartId={V2_CHART_IDS.energyMonthly}
              height="260px"
              filter={buildFilter(yearRange.from, yearRange.to)}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 3 — Active Users (own filter) + New Users (fixed 30d) */}
      <Row style={{ marginBottom: 12 }}>
        <Col md={6}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>Active Users</span>
              <select
                value={activeUserPeriod}
                onChange={(e) => setActiveUserPeriod(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: '1px solid #3b4a56',
                  background: '#1b2a38',
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {['Daily', 'Weekly', 'Monthly', 'Total'].map((group) => (
                  <optgroup key={group} label={group} style={{ background: '#1b2a38', color: '#8d949b' }}>
                    {ACTIVE_USER_PERIODS.filter((p) => p.group === group).map(({ label, value }) => (
                      <option key={value} value={value} style={{ background: '#1b2a38', color: '#fff' }}>
                        {label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span style={{ fontWeight: 400, fontSize: 12, color: '#8d949b', marginLeft: 'auto' }}>
                {getRangeLabel(activeUserPeriod)}
              </span>
            </div>
            <ChartEmbed
              chartId={V2_CHART_IDS.activeUsers}
              height="120px"
              filter={buildFilter(activeUserRange.from, activeUserRange.to)}
            />
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              New Users&nbsp;
              <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>
                ({moment().subtract(29, 'days').format('MMM DD')} – {moment().format('MMM DD, YYYY')})
              </span>
            </div>
            <ChartEmbed
              chartId={V2_CHART_IDS.newUsers}
              height="120px"
              filter={userFilter(moment().subtract(29, 'days').startOf('day').toDate(), moment().endOf('day').toDate())}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 4 — Performance Metrics (BE API) */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Performance Metrics</div>
        <PerformanceMetrics />
      </div>

      {/* Row 5 — Station Tables (BE API) */}
      <StationTables />
    </div>
  );
};

export default DashboardV2Section;
